import React from "react";
import "./VitalCard.css";

/**
 * VitalCard — at-a-glance metric card for the customer detail vitals strip.
 *
 * Per CUST-DETAIL-POLISH-002 design spec (`docs/design/CUST-DETAIL-POLISH-002-design.md`).
 *
 * Render-state precedence (highest to lowest):
 *   error  >  loading  >  empty  >  default
 *
 * @param {Object} props
 * @param {string} props.label                        Card label (rendered uppercase via CSS)
 * @param {string|number} [props.value]               Headline metric (skipped during loading)
 * @param {string} [props.helper]                     Supporting copy below the value
 * @param {Function} [props.onClick]                  When provided + not empty/loading/error: card becomes a button
 * @param {string} [props.targetTab]                  Aria description of the click destination (e.g., "Session Credits tab")
 * @param {boolean} [props.loading=false]             Render skeleton state
 * @param {boolean} [props.empty=false]               Render zero/empty state (suppresses onClick)
 * @param {string} [props.error]                      Error message (renders error treatment, suppresses onClick)
 * @param {"gold"|"teal"|"default"} [props.accent]    Left accent stripe color
 * @param {string} [props.className]                  Additional class hook for parent layout
 */
const VitalCard = ({
  label,
  value,
  helper,
  onClick,
  targetTab,
  loading = false,
  empty = false,
  error,
  accent = "default",
  className = "",
}) => {
  const isError = Boolean(error);
  const isLoading = !isError && loading;
  const isEmpty = !isError && !isLoading && empty;
  const isClickable =
    typeof onClick === "function" && !isLoading && !isEmpty && !isError;

  const accentClass =
    accent === "gold"
      ? "vital-card--accent-gold"
      : accent === "teal"
      ? "vital-card--accent-teal"
      : "";

  const classes = [
    "vital-card",
    accentClass,
    isClickable ? "vital-card--clickable" : "",
    isLoading ? "vital-card--loading" : "",
    isEmpty ? "vital-card--empty" : "",
    isError ? "vital-card--error" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const ariaLabel = isLoading
    ? `Loading ${label}`
    : isEmpty
    ? `${label}: not available`
    : isError
    ? `${label}: couldn't load`
    : isClickable && targetTab
    ? `${label}: ${value}. Open ${targetTab}.`
    : undefined;

  if (isLoading) {
    return (
      <div
        className={classes}
        aria-busy="true"
        aria-label={ariaLabel}
        role="status"
        data-testid="vital-card-skeleton"
      >
        <div className="vital-card__skeleton vital-card__skeleton--label" />
        <div className="vital-card__skeleton vital-card__skeleton--value" />
        <div className="vital-card__skeleton vital-card__skeleton--helper" />
      </div>
    );
  }

  const content = (
    <>
      <span className="vital-card__label">{label}</span>
      <span className="vital-card__value">
        {isError ? "—" : value}
      </span>
      <span className="vital-card__helper">
        {isError ? "Couldn't load" : helper}
      </span>
    </>
  );

  if (isClickable) {
    return (
      <button
        type="button"
        className={classes}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={classes}
      aria-label={ariaLabel}
      title={isError ? "Refresh the page to try again." : undefined}
    >
      {content}
    </div>
  );
};

export default VitalCard;
